import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listPackOrders, listPackOrdersGrouped } from "@/lib/pack.functions";
import { findOrderByTote } from "@/lib/pick.functions";
import { listPackStations } from "@/lib/devices.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { ScopeBar } from "@/components/scope-bar";
import { ScanInput } from "@/components/ui/scan-input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { PackageOpen, Boxes, Layers, Sparkles, Box, MapPin, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/pack/")({ component: PackIndex });

function PackIndex() {
  const { tenantId, facilityId, customerId, customers } = useScope();
  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";
  const listFn = useServerFn(listPackOrders);
  const groupedFn = useServerFn(listPackOrdersGrouped);
  const findByToteFn = useServerFn(findOrderByTote);
  const stationsFn = useServerFn(listPackStations);
  const navigate = useNavigate();

  const storageKey = facilityId ? `pack-station:${facilityId}` : null;
  const [station, setStation] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState("");
  const [customStation, setCustomStation] = useState("");

  const { data: stationData } = useQuery({
    queryKey: ["pack-stations", tenantId, facilityId],
    enabled: !!tenantId,
    queryFn: () => stationsFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null } }),
  });
  const stations = stationData?.stations ?? [];

  useEffect(() => {
    if (!storageKey) return;
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
    if (saved) {
      setStation(saved);
    } else {
      setPickerOpen(true);
    }
  }, [storageKey]);

  const confirmStation = () => {
    const choice = (pickerValue === "__custom__" ? customStation : pickerValue).trim();
    if (!choice || !storageKey) return;
    window.localStorage.setItem(storageKey, choice);
    setStation(choice);
    setPickerOpen(false);
    setPickerValue("");
    setCustomStation("");
    toast.success(`Pack station set to ${choice}`);
  };

  const { data = [], isLoading } = useQuery({
    queryKey: ["pack-orders", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });
  const { data: grouped } = useQuery({
    queryKey: ["pack-orders-grouped", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => groupedFn({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });

  const onToteScan = async (raw: string) => {
    const code = raw.trim();
    if (!code || !facilityId) return;
    try {
      const res = await runServerFn(
        () => findByToteFn({ data: { facilityId, code } }),
        "Lookup tote",
      );
      if (!res.orderId) return toast.error(`No order assigned to tote ${code}`);
      navigate({ to: "/app/pack/$orderId", params: { orderId: res.orderId } });
    } catch {
      /* toast handled */
    }
  };

  return (
    <>
      <PageHeader
        title="Pack Station"
        description="Pick up an order, scan units into cartons, and ship."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/app/pack/bulk">
              <Button variant="outline" size="sm">
                <Printer className="h-4 w-4 mr-1" />Print queue
              </Button>
            </Link>
            <ScopeBar />
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        <div className="mb-3"><ContextualTip tipKey="packing_intro" /></div>
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border bg-card p-3">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            {station ? (
              <>
                <span className="text-muted-foreground">You're at</span>
                <span className="font-semibold">{station}</span>
                <span className="text-muted-foreground">— labels print to this station's printer.</span>
              </>
            ) : (
              <span className="text-muted-foreground">No pack station selected.</span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => { setPickerValue(station ?? ""); setPickerOpen(true); }}>
            {station ? "Change" : "Select station"}
          </Button>
        </div>

        <div className="mb-4 rounded-2xl border-2 border-scan/40 bg-scan/5 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2">
            <Boxes className="h-5 w-5 text-scan" />
            <div className="text-sm font-semibold">Scan a tote to begin packing</div>
          </div>
          <ScanInput onScan={onToteScan} placeholder="Scan tote code…" size="lg" autoFocus />
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All ({data.length})</TabsTrigger>
            <TabsTrigger value="identical">
              <Layers className="h-3.5 w-3.5 mr-1" /> Identical ({grouped?.identical.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="in_common">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> In-common ({grouped?.in_common.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="unique">
              <Box className="h-3.5 w-3.5 mr-1" /> Unique ({grouped?.unique.length ?? 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <OrderList orders={data} cName={cName} isLoading={isLoading} />
          </TabsContent>

          <TabsContent value="identical" className="space-y-3">
            {(grouped?.identical ?? []).length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border rounded-lg bg-card text-sm">
                No identical-order groups to batch-pack.
              </div>
            ) : (grouped?.identical ?? [])
              .filter((g) => g.orders.length > 0)
              .map((g) => (
              <div key={g.key} className="border rounded-lg bg-card p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold">{g.customer_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {g.orders.length} identical orders ·{" "}
                      {g.shared_skus.map((s) => `${s.sku_code} ×${s.qty}`).join(", ")}
                    </div>
                  </div>
                  <Link
                    to="/app/pack/batch"
                    search={{ ids: g.orders.map((o) => o.id).join(",") }}
                  >
                    <Button size="touch">Batch pack {g.orders.length} →</Button>
                  </Link>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {g.orders.map((o) => (
                    <Badge key={o.id} variant="secondary" className="font-mono text-[11px]">
                      {o.order_number}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="in_common">
            <div className="mb-2 text-xs text-muted-foreground">
              These orders share SKUs but aren't identical. Pack one at a time.
            </div>
            <OrderList orders={grouped?.in_common ?? []} cName={cName} isLoading={!grouped} />
          </TabsContent>

          <TabsContent value="unique">
            <OrderList orders={grouped?.unique ?? []} cName={cName} isLoading={!grouped} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o && !station) { navigate({ to: "/app/operations/desk" }); return; } setPickerOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Which pack station are you at?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Pack station</Label>
            {stations.length > 0 ? (
              <Select value={pickerValue} onValueChange={setPickerValue}>
                <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  {stations.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value="__custom__">Other…</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">No stations configured yet. Type one below (admins can wire it to a printer under Admin → Devices).</p>
                <Input value={customStation} onChange={(e) => { setCustomStation(e.target.value); setPickerValue("__custom__"); }} placeholder="Pack Station #1" />
              </>
            )}
            {pickerValue === "__custom__" && stations.length > 0 && (
              <Input value={customStation} onChange={(e) => setCustomStation(e.target.value)} placeholder="Pack Station #1" />
            )}
          </div>
          <DialogFooter>
            <Button onClick={confirmStation}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrderList({
  orders,
  cName,
  isLoading,
}: {
  orders: Array<{ id: string; order_number: string; status?: string; customer_id: string; carrier?: string | null; service_level?: string | null }>;
  cName: (id: string) => string;
  isLoading: boolean;
}) {
  return (
    <>
      <div className="space-y-2 sm:hidden">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground border rounded-lg bg-card">
              <PackageOpen className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <div className="text-sm">No orders ready to pack.</div>
            </div>
          ) : orders.map((o) => (
            <Link
              key={o.id}
              to="/app/pack/$orderId"
              params={{ orderId: o.id }}
              className="block border rounded-lg bg-card p-3 active:bg-muted"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium font-mono text-sm">{o.order_number}</span>
                {o.status ? <StatusPill tone={statusTone(o.status)} dot>{o.status}</StatusPill> : null}
              </div>
              <div className="text-xs text-muted-foreground truncate mt-1">{cName(o.customer_id)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {o.carrier ?? "—"}{o.service_level ? ` · ${o.service_level}` : ""}
              </div>
            </Link>
          ))}
        </div>
        <div className="hidden sm:block border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Customer</TableHead><TableHead>Carrier</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : orders.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  <PackageOpen className="h-6 w-6 mx-auto mb-2 opacity-50" />No orders ready to pack.
                </TableCell></TableRow>
              ) : orders.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-medium font-mono">{o.order_number}</TableCell>
                  <TableCell className="text-muted-foreground">{cName(o.customer_id)}</TableCell>
                  <TableCell>{o.carrier ?? "—"}{o.service_level ? ` · ${o.service_level}` : ""}</TableCell>
                  <TableCell>{o.status ? <StatusPill tone={statusTone(o.status)} dot>{o.status}</StatusPill> : null}</TableCell>
                  <TableCell className="text-right">
                    <Link to="/app/pack/$orderId" params={{ orderId: o.id }}>
                      <Button size="touch">Pack →</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
    </>
  );
}